import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import { Constants } from 'src/shared/utilities/constants.util';
import { EntityAdminConfig } from 'src/shared/Baseclasses/entity-admin.component';
import { PageLayoutService } from 'src/shared/services/page-layout.service';
import { VarianceReason } from 'src/shared/models/variance-reason.model';
import { VarianceReasonService } from 'src/shared/services/variance-reason.service';
import { RemoveMasterListFailure } from 'src/shared/enum/remove-master-list-failure.enum';

@Component({
    selector: 'smt-variance-reason',
    templateUrl: './variance-reason.component.html',
    styleUrls: ['./variance-reason.component.scss']
})
export class VarianceReasonComponent extends EntityAdminConfig implements OnDestroy, OnInit {
    @ViewChild('varianceReasonEditForm') public varianceReasonEditForm: NgForm;

    varianceReasons: VarianceReason[];
    mostRecent: boolean;
    selectedCode: string;
    selectedDescription: string;
    selectedReason: string;
    selectedDefault: boolean;
    selectedDeptLeaderPrompt: string;
    selectedCreatedApplicationUserId : string;
    showCostCenterWarning: boolean = false;
    costCenterNames: string[];
    costCenterListModalActive: boolean = false;
    varianceReasonName:string;

    constructor(private _varianceReasonService: VarianceReasonService, private _pageLayout: PageLayoutService) { 
        super(_pageLayout); 
    }

    ngOnDestroy() {
        super.cleanUp();
	}

    ngOnInit() {
        super.initialize(
            Constants.VarianceReasons,
            [ "Reason","Code", "Default", "Last Modified" ],
            async() => this.setSelectedVarianceReasonValues(), 
            async(mostRecent: boolean) => this.refreshVarianceReasonList(mostRecent), 
            async() => this.appendVarianceReasonList(),
            async() => this.saveVarianceReason(),
            () => this.setCurrentSelectionLabel(),
            () => { return "Remove"; });
    }

    async appendVarianceReasonList(): Promise<void> {
		let result = await this._varianceReasonService.getVarianceReasonsInfiniteScroll(
                                                    false,
                                                    this.offset, 
                                                    this.itemsPerPage, 
                                                    this.searchText, 
                                                    this.selectedFilter, 
                                                    this.getSortOrderString());

		this.varianceReasons = this.varianceReasons.concat(result);		
    }

    onAddVarianceReasonClick() {    
		this.selection = { 
			id: 0,
			code: '',
            description: '',
            default: false,
            deptLeaderPrompt: '',
			modifiedByUserName: '',
            modifiedDate: null,
            reason: '',
            createdApplicationUserId: null
		};

        this.showAdminActionWindow = true;
        super.limitAdminActionLabel();
        this.setSelectedVarianceReasonValues();
        this.showCostCenterWarning = false;
    }

    onCancel() {
        super.onCancel();
		this.setSelectedVarianceReasonValues();
		this.resetForm();
    }

    onSelection(reason: VarianceReason) {
        this.resetForm();
        super.onSelection(reason);
    }

    async onRemoveVarianceReasonClick() {
        if (!this.selection) {
            return;
        }

      let result = await this._varianceReasonService.removeVarianceReason(this.selection.id);
      if (result == RemoveMasterListFailure.CostCenterAssociation)
        this.showCostCenterWarning = true;
        this.confirmModalActive = false;

      if (result == RemoveMasterListFailure.None) {
            super.refreshAdminList(false, false);
        }
        else {
            super.setErrorMessage("Variance reason delete failed.");
        }
    }
    async getCostCentersByVarianceReasonId(openModal:boolean) {
        if (!this.selection) {
          return;
        }
      this.varianceReasonName=this.selection.reason;
      this.costCenterNames = await this._varianceReasonService.getCostCentersByVarianceReasonId(this.selection.id);
      this.costCenterListModalActive = openModal;
      }
    async refreshVarianceReasonList(mostRecent: boolean): Promise<VarianceReason[]> {    
        let result = await this._varianceReasonService.getVarianceReasonsInfiniteScroll(
                                                        mostRecent,
                                                        this.offset, 
                                                        this.itemsPerPage, 
                                                        this.searchText, 
                                                        this.selectedFilter, 
                                                        this.getSortOrderString());

        this.varianceReasons = result;

        this.setSelectedVarianceReasonValues();
        this.resetForm();

        return result;
    }

    resetForm() {
		if (!this.varianceReasonEditForm) {
			return;
		}

		Object.keys(this.varianceReasonEditForm.controls).forEach(control => {
			this.varianceReasonEditForm.controls[control].markAsPristine();
		});
    }
  
    async saveVarianceReason() {
        if (!this.selection) { 
                return; 
        }

        let mostRecent = this.selection.id == 0;

        let selectedVarianceReason = new VarianceReason;

        selectedVarianceReason.id = this.selection.id;
        selectedVarianceReason.createdApplicationUserId = this.selection.createdApplicationUserId;
        selectedVarianceReason.code = this.selectedCode;
        selectedVarianceReason.description = this.selectedDescription;
        selectedVarianceReason.reason = this.selectedReason;
        selectedVarianceReason.default = this.selectedDefault;
        selectedVarianceReason.deptLeaderPrompt = this.selectedDeptLeaderPrompt;

            let result = await this._varianceReasonService.setVarianceReason(selectedVarianceReason);
            if (result.errorMessage) {
                super.setErrorMessage(result.errorMessage);
            }
            else if(result.id>0){
            this.selection.id = result.id; 
            this.selection.createdApplicationUserId = result.createdApplicationUserId;
            this.selection.code = result.code;
            this.selection.description = result.description; 
            this.selection.reason = result.reason;
            this.selection.default = result.default;
            this.selection.deptLeaderPrompt = result.deptLeaderPrompt;
            await super.refreshAdminList(mostRecent, true);
            }
            else {
                super.setErrorMessage("Variance reason failed to save.");
            }
        }          

    setCurrentSelectionLabel(): string {
        if (!this.selection) {
            return "";
        }

        if (this.selection.id == 0) {
        return "New Reason";
        }

        return this.selection.reason;
    }

    setSelectedVarianceReasonValues() {
        super.setErrorMessage("");

        if (this.selection) {
            this.selectedCode = this.selection.code;
            this.selectedCreatedApplicationUserId = this.selection.createdApplicationUserId;
            this.selectedDescription = this.selection.description;
            this.selectedReason = this.selection.reason;
            this.selectedDefault = this.selection.default;
            this.selectedDeptLeaderPrompt = this.selection.deptLeaderPrompt;
        }
        else {
            this.selectedCode = "";
            this.selectedDescription = "";
            this.selectedReason = "";
            this.selectedDefault = false;
            this.selectedDeptLeaderPrompt = "";
            this.selectedCreatedApplicationUserId = "";
        }
      this.showCostCenterWarning=false;
    }
}
